export const queryKeys = {
  /**
   * The caller's own permissions. The route takes no id; `who` is the signed-in
   * operator, so one session's answer is never served to the next.
   */
  me: {
    capabilities: (who: string | null = null) => ['me', 'capabilities', who] as const,
  },
  /**
   * #195 — the global audit log. A root of its own rather than a branch of
   * `staff`: what it holds is not the staff's record, it is every sensitive
   * decision the platform made.
   */
  audit: {
    list: (q: unknown) => ['audit', 'list', q] as const,
  },
  analytics: {
    kpi: () => ['analytics', 'kpi'] as const,
    revenueSeries: (range: string) => ['analytics', 'revenue-series', range] as const,
    activity: () => ['analytics', 'activity'] as const,
    fraud: () => ['analytics', 'fraud'] as const,
    topSellers: (window: string) => ['analytics', 'top-sellers', window] as const,
    categoryBreakdown: (window: string) => ['analytics', 'category-breakdown', window] as const,
    kycFunnel: () => ['analytics', 'kyc-funnel'] as const,
    tierDistribution: () => ['analytics', 'tier-distribution'] as const,
  },
  users: {
    list: (q: unknown) => ['users', 'list', q] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
    // #84 — cola de apelaciones de suspensión (no confundir con disputes.appeals).
    suspensionAppeals: (q: unknown) => ['users', 'suspension-appeals', q] as const,
    // ko-backend#818 — under `users` so the invalidator every user write runs
    // (`['users']`) refetches the score alongside the detail.
    risk: (id: string) => ['users', 'risk', id] as const,
  },
  /** Catálogo visual de tiers (ko-backend#453). Los umbrales van en `koins`. */
  tierCatalog: {
    list: (module?: string) => ['tier-catalog', 'list', module] as const,
    styles: () => ['tier-catalog', 'styles'] as const,
  },

  koins: {
    balance: (userId: string) => ['koins', 'balance', userId] as const,
    events: (userId: string, state?: string, module?: string) =>
      ['koins', 'events', userId, state, module] as const,
    cohorts: (windowDays: number) => ['koins', 'cohorts', windowDays] as const,
    flagged: (minScore?: number) => ['koins', 'flagged', minScore] as const,
    policyVersions: (module?: string) => ['koins', 'policy-versions', module] as const,
    thresholds: (module?: string) => ['koins', 'thresholds', module] as const,
  },
  kyc: {
    queue: (q: unknown) => ['kyc', 'queue', q] as const,
  },
  trades: {
    // Shared prefix: invalidates listing, queue and detail in one go.
    all: () => ['trades'] as const,
    list: (q: unknown) => ['trades', 'list', q] as const,
    byUser: (userId: string) => ['trades', 'byUser', userId] as const,
    detail: (id: string) => ['trades', 'detail', id] as const,
  },
  listings: {
    queue: (q: unknown) => ['listings', 'queue', q] as const,
    detail: (id: string) => ['listings', 'detail', id] as const,
    byListingId: (id: string) => ['listings', 'by-listing-id', id] as const,
  },
  tournaments: {
    // Prefix every tournament mutation invalidates. Resolving a result appeal
    // can promote the tournament's result to FINAL, so the list has to go too.
    all: () => ['tournaments'] as const,
    list: (q: unknown) => ['tournaments', 'list', q] as const,
    detail: (id: string) => ['tournaments', 'detail', id] as const,
    participants: (id: string) => ['tournaments', 'participants', id] as const,
    prizes: (id: string) => ['tournaments', 'prizes', id] as const,
    prizeDelivery: (prizeId: string) => ['tournaments', 'prizeDelivery', prizeId] as const,
    standings: (id: string) => ['tournaments', 'standings', id] as const,
    bracket: (id: string) => ['tournaments', 'bracket', id] as const,
    tiebreakSeed: (id: string) => ['tournaments', 'tiebreak-seed', id] as const,
    statusCounts: () => ['tournaments', 'status-counts'] as const,
    questionCapacity: (q: unknown) => ['tournaments', 'question-capacity', q] as const,
    resultAppeals: (q: unknown) => ['tournaments', 'result-appeals', q] as const,
    // Under the `tournaments` prefix so a match override invalidates alongside
    // the bracket it belongs to. Fetching this writes an audit row, so nothing
    // must ever prefetch or poll it.
    matchVoters: (matchId: string) => ['tournaments', 'match-voters', matchId] as const,
  },
  disputes: {
    all: () => ['disputes'] as const,
    list: (q: unknown) => ['disputes', 'list', q] as const,
    detail: (id: string) => ['disputes', 'detail', id] as const,
    appeals: (q: unknown) => ['disputes', 'appeals', q] as const,
    frozenEscrow: () => ['disputes', 'frozen-escrow'] as const,
  },
  staff: {
    list: (q: unknown) => ['staff', 'list', q] as const,
    detail: (userId: string) => ['staff', 'detail', userId] as const,
    audit: (userId: string) => ['staff', 'audit', userId] as const,
    auditGlobal: (q: unknown) => ['staff', 'audit-global', q] as const,
    tierHistory: (userId: string) => ['staff', 'tier-history', userId] as const,
    tierRules: () => ['staff', 'tier-rules'] as const,
    profiles: () => ['staff', 'profiles'] as const,
    summary: () => ['staff', 'summary'] as const,
    roles: () => ['staff', 'roles'] as const,
    admins: () => ['staff', 'admins'] as const,
  },
  questions: {
    list: (q: unknown) => ['questions', 'list', q] as const,
    detail: (id: string) => ['questions', 'detail', id] as const,
    flagged: (q: unknown) => ['questions', 'flagged', q] as const,
    metrics: (id: string) => ['questions', 'metrics', id] as const,
    mediaRights: (id: string) => ['questions', 'media-rights', id] as const,
  },
  catalogs: {
    banks: () => ['catalogs', 'banks'] as const,
    categories: () => ['catalogs', 'categories'] as const,
  },
  // Question banks and their own taxonomy. Kept apart from `taxonomy`, which is
  // the marketplace tree, so invalidating one never refetches the other.
  questionBanks: {
    all: () => ['question-banks'] as const,
    // `includeArchived` is part of the key: the archived and non-archived
    // listings are different server responses, not a client-side filter.
    list: (includeArchived: boolean) => ['question-banks', 'list', includeArchived] as const,
    detail: (id: string) => ['question-banks', 'detail', id] as const,
  },
  questionCategories: {
    all: () => ['question-categories'] as const,
    list: (includeArchived: boolean) => ['question-categories', 'list', includeArchived] as const,
  },
  proposals: {
    queue: (q: unknown) => ['proposals', 'queue', q] as const,
  },
  platform: {
    flags: () => ['platform', 'flags'] as const,
    flagCatalog: () => ['platform', 'flags', 'catalog'] as const,
    config: () => ['platform', 'config'] as const,
    configCatalog: () => ['platform', 'config', 'catalog'] as const,
    // Under `config` so an edit on the configuration screen invalidates it too.
    sectionConfig: () => ['platform', 'config', 'section'] as const,
  },
  exports: {
    list: () => ['exports', 'list'] as const,
  },
  // ko-admin#375 — the store's published version, per platform. Nothing in
  // the panel writes it (it is the backend's env), so nothing invalidates it.
  app: {
    versionPolicy: (platform: string) => ['app', 'version', platform] as const,
  },
  // ko-admin#213 — the Panel's status row; a key of its own, not under
  // `platform`, so a write to a switch or a config key can invalidate it by name.
  platformStatus: () => ['platform-status'] as const,
  orders: {
    // Shared prefix every order write already invalidates: a force-cancel or a
    // mark-delivered changes both the list and the detail of the same order.
    all: () => ['orders'] as const,
    list: (q: unknown) => ['orders', 'list', q] as const,
    detail: (id: string) => ['orders', 'detail', id] as const,
  },
  // ko-admin#146 — top-up clawbacks awaiting a release/settle decision. Its own
  // prefix rather than a child of `disputes`: a reversed top-up opens no
  // dispute, which is exactly why nothing in the panel could reach it.
  wallet: {
    all: () => ['wallet'] as const,
    pendingTopupClawbacks: () => ['wallet', 'topup-clawbacks', 'pending'] as const,
    // ko-admin#152 — a user's money. Under the `wallet` prefix and not under
    // `users`, so resolving a clawback invalidates the balance it just moved.
    userWallet: (userId: string) => ['wallet', 'user', userId] as const,
    payouts: (q: unknown) => ['wallet', 'payouts', q] as const,
    payoutDetail: (id: string) => ['wallet', 'payouts', 'detail', id] as const,
  },
  strikes: {
    byUser: (userId: string, q: unknown) => ['strikes', 'byUser', userId, q] as const,
    all: (q: unknown) => ['strikes', 'all', q] as const,
    stats: () => ['strikes', 'stats'] as const,
  },
  // A-23 — vista global de entrega de premios. Mismos literales que usaba la
  // feature (prizes-api.ts) para no invalidar caches existentes.
  prizes: {
    all: () => ['prizes'] as const,
    globalList: (q: unknown) => ['prizes', 'global-list', q] as const,
    stats: () => ['prizes', 'stats'] as const,
    delivery: (prizeId: string) => ['prizes', 'delivery', prizeId] as const,
    // #299 — cash owed to winners that has not moved yet.
    pendingReleases: () => ['prizes', 'pending-releases'] as const,
  },
  // #339 — hitos comunitarios (config del sistema KOins).
  milestones: {
    list: () => ['milestones', 'list'] as const,
  },
  campaigns: {
    list: (q: unknown) => ['campaigns', 'list', q] as const,
    detail: (id: string) => ['campaigns', 'detail', id] as const,
    estimate: (filters: unknown) => ['campaigns', 'estimate', filters] as const,
    deepLinkRoutes: () => ['campaigns', 'deep-link-routes'] as const,
  },
  taxonomy: {
    tree: () => ['taxonomy', 'tree'] as const,
    category: (id: string) => ['taxonomy', 'category', id] as const,
  },
  invites: {
    list: () => ['invites', 'list'] as const,
  },
  // #83 — user-report MODERATION queue (admin/moderation/reports). Not the
  // same thing as `analytics` above, which feeds the panel's /reports section.
  // `all()` is the prefix every mutation invalidates: the list key carries the
  // filter object, so invalidating one filtered list would leave the other
  // chips showing a report that is no longer OPEN.
  // #60 — anti-cheat review, keyed by session id because the backend offers no
  // list. `all()` is the prefix every mutation invalidates.
  anticheat: {
    all: () => ['anticheat'] as const,
    detail: (sessionId: string) => ['anticheat', 'detail', sessionId] as const,
    reveal: (sessionId: string) => ['anticheat', 'reveal', sessionId] as const,
    // The review queue. Keyed by the side being looked at and by the cursor,
    // so paging forward does not overwrite the page behind it in the cache.
    queue: (flagged: boolean, cursor: string | null, tournamentId: string | null = null) =>
      ['anticheat', 'queue', flagged, cursor, tournamentId] as const,
  },
  moderation: {
    all: () => ['moderation'] as const,
    reports: (q: unknown) => ['moderation', 'reports', q] as const,
    report: (id: string) => ['moderation', 'report', id] as const,
  },
  // ko-backend#748 — the SLA tray. One key: the route takes no parameters, and
  // acknowledging a breach removes a row from that same one list, so the ack
  // invalidates exactly this.
  reviewSla: {
    breaches: () => ['review-sla', 'breaches'] as const,
  },
  // ko-backend#800 — the outbox's dead letters. `all()` is the prefix a retry
  // invalidates: the requeued row has zero attempts again and leaves the stuck
  // list on the next read.
  outbox: {
    all: () => ['outbox'] as const,
    stuck: (q: unknown) => ['outbox', 'stuck', q] as const,
  },
  // ko-backend#777 — the tray of money decisions waiting on a person. `all()`
  // is the prefix every one of the three writes invalidates: each of them
  // takes a row out of the list, whichever `kind` filter is showing it.
  pendingDecisions: {
    all: () => ['pending-decisions'] as const,
    list: (kind?: string) => ['pending-decisions', 'list', kind] as const,
  },
  // ko-admin#344 — the alerts the backend sent the signed-in admin (their own
  // MODERATION inbox). Marking one read refetches the list.
  teamAlerts: {
    all: () => ['team-alerts'] as const,
    list: () => ['team-alerts', 'list'] as const,
  },
} as const;
